import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Car, Package, TrendUp, CurrencyEur, Fire, ArrowRight, Plus, Clock } from '@phosphor-icons/react';
import axios from 'axios';
import AdminLayout from '../../components/admin/AdminLayout';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ProDashboard = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API_URL}/api/pro/dashboard`).then(r => { setData(r.data); setLoading(false); }).catch(() => setLoading(false));
  }, []);

  if (loading) return <AdminLayout title="Dashboard Pro"><div className="flex items-center justify-center h-64"><div className="w-8 h-8 border-2 border-[#E63946] border-t-transparent rounded-full animate-spin" /></div></AdminLayout>;

  const stats = data?.stats || {};
  const statCards = [
    { label: 'Véhicules en stock', value: stats.active_vehicles || 0, icon: Car, color: '#E63946' },
    { label: 'Pièces disponibles', value: stats.total_parts || 0, icon: Package, color: '#2A9D8F' },
    { label: 'Pièces vendues', value: stats.total_sold || 0, icon: TrendUp, color: '#E63946' },
    { label: 'Valeur du stock', value: `${(stats.total_stock_value || 0).toLocaleString('fr-FR')} €`, icon: CurrencyEur, color: '#2A9D8F' },
  ];

  const getDemandColor = (score) => {
    if (score >= 8) return 'text-green-600 bg-green-50';
    if (score >= 6) return 'text-blue-600 bg-blue-50';
    if (score >= 4) return 'text-amber-600 bg-amber-50';
    return 'text-gray-500 bg-gray-50';
  };

  const getDemandBar = (score) => {
    const pct = Math.min(score * 10, 100);
    const color = score >= 8 ? '#22c55e' : score >= 6 ? '#3b82f6' : score >= 4 ? '#f59e0b' : '#9ca3af';
    return <div className="w-full bg-gray-100 h-2 rounded-full"><div className="h-2 rounded-full" style={{ width: `${pct}%`, backgroundColor: color }} /></div>;
  };

  return (
    <AdminLayout title="Dashboard Pro">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        {statCards.map((s, i) => {
          const Icon = s.icon;
          return (
            <div key={i} className="bg-white border border-gray-200 p-6 shadow-sm" data-testid={`pro-stat-${i}`}>
              <div className="flex items-start justify-between">
                <div><p className="text-gray-500 text-sm mb-2">{s.label}</p><p className="font-['Outfit'] text-3xl font-bold text-gray-900">{s.value}</p></div>
                <div className="w-12 h-12 flex items-center justify-center" style={{ backgroundColor: `${s.color}15` }}><Icon size={24} style={{ color: s.color }} /></div>
              </div>
            </div>
          );
        })}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Top demanded parts */}
        <div className="lg:col-span-2">
          <div className="bg-white border border-gray-200 shadow-sm">
            <div className="p-6 border-b border-gray-200 flex items-center justify-between">
              <h2 className="font-['Outfit'] text-lg font-bold text-gray-900 flex items-center gap-2">
                <Fire size={20} className="text-[#E63946]" />
                Score de demande — Pièces en stock
              </h2>
            </div>
            <div className="divide-y divide-gray-100">
              {(data?.top_demand_parts || []).length > 0 ? data.top_demand_parts.map((p, i) => (
                <div key={i} className="p-4 flex items-center gap-4" data-testid={`demand-part-${i}`}>
                  <span className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center text-xs font-bold text-gray-500">{i + 1}</span>
                  <div className="flex-1 min-w-0">
                    <p className="text-gray-900 font-medium text-sm truncate">{p._id}</p>
                    <div className="flex items-center gap-3 mt-1">
                      <div className="flex-1">{getDemandBar(p.avg_demand)}</div>
                      <span className={`text-xs font-bold px-2 py-0.5 ${getDemandColor(p.avg_demand)}`}>
                        {p.avg_demand >= 8 ? 'Très demandé' : p.avg_demand >= 6 ? 'Demandé' : p.avg_demand >= 4 ? 'Moyen' : 'Faible'}
                      </span>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-[#2A9D8F] font-bold text-sm">{(p.avg_price || 0).toFixed(0)} €</p>
                    <p className="text-gray-400 text-xs">{p.count} en stock</p>
                  </div>
                </div>
              )) : <div className="p-8 text-center text-gray-400">Aucune pièce en stock. Ajoutez un véhicule pour commencer.</div>}
            </div>
          </div>
        </div>

        {/* Actions + Recent vehicles */}
        <div>
          <Link
            to="/admin/pro/reception"
            className="block w-full bg-[#E63946] text-white p-6 shadow-sm hover:bg-[#D90429] transition-colors mb-6"
            data-testid="new-vehicle-btn"
          >
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 bg-white/20 flex items-center justify-center rounded-lg"><Plus size={28} weight="bold" /></div>
              <div>
                <p className="font-['Outfit'] text-lg font-bold">Réceptionner un véhicule</p>
                <p className="text-white/70 text-sm mt-1">Photos + VIN → catalogue automatique</p>
              </div>
            </div>
          </Link>

          <div className="bg-white border border-gray-200 shadow-sm">
            <div className="p-6 border-b border-gray-200 flex items-center justify-between">
              <h2 className="font-['Outfit'] text-lg font-bold text-gray-900">Derniers véhicules</h2>
              <Link to="/admin/pro/stock" className="text-[#E63946] text-sm font-medium hover:underline flex items-center gap-1">Tout voir <ArrowRight size={14} /></Link>
            </div>
            <div className="divide-y divide-gray-100">
              {(data?.recent_vehicles || []).length > 0 ? data.recent_vehicles.map((v, i) => (
                <Link key={i} to={`/admin/pro/vehicle/${v.id}`} className="p-4 flex items-center justify-between hover:bg-gray-50 block" data-testid={`recent-vehicle-${i}`}>
                  <div>
                    <p className="text-gray-900 font-medium text-sm">{v.brand || 'Véhicule'} {v.model || ''} {v.year || ''}</p>
                    <p className="text-gray-400 text-xs flex items-center gap-1 mt-1"><Clock size={12} />{new Date(v.created_at).toLocaleDateString('fr-FR')}</p>
                  </div>
                  <StatusBadge status={v.status} />
                </Link>
              )) : <div className="p-8 text-center text-gray-400">Aucun véhicule</div>}
            </div>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
};

const StatusBadge = ({ status }) => {
  const styles = {
    received: 'bg-amber-100 text-amber-700',
    analyzing: 'bg-blue-100 text-blue-700',
    cataloged: 'bg-green-100 text-green-700',
    active: 'bg-[#2A9D8F]/10 text-[#2A9D8F]',
    error: 'bg-red-100 text-red-700',
    archived: 'bg-gray-100 text-gray-500',
  };
  const labels = {
    received: 'Reçu',
    analyzing: 'Analyse...',
    cataloged: 'Catalogué',
    active: 'En vente',
    error: 'Erreur',
    archived: 'Archivé',
  };
  return <span className={`px-2 py-1 text-xs font-bold uppercase ${styles[status] || styles.received}`}>{labels[status] || status}</span>;
};

export default ProDashboard;
